import { Component, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { NgxSpinnerService } from "ngx-spinner";
import Swal from "sweetalert2";
import { HttpHelpService } from "../service/http-help.service";

type Lang = "ar" | "en" | "he";

@Component({
	selector: "app-expense-plans",
	templateUrl: "./expense-plans.component.html",
	styleUrls: ["./expense-plans.component.scss"],
})
export class ExpensePlansComponent implements OnInit {
	curentLang: Lang = (localStorage.getItem("curentLang") as Lang) || "ar";

	merchantOptions: any[] = [];
	tokenExtensionPlans: any[] = [];

	planSearch: string = "";
	isEditingPlan = false;
	editingPlanId: number | null = null;

	lastPaymentUrl: string | null = null;
	lastOrderId: string | null = null;

	planForm = new FormGroup({
		name_en: new FormControl("", [Validators.required]),
		name_ar: new FormControl(""),
		name_he: new FormControl(""),
		extra_tokens: new FormControl<number | null>(null, [Validators.required, Validators.min(1)]),
		price: new FormControl<number | null>(null, [Validators.required, Validators.min(0.01)]),
		is_active: new FormControl(true),
		sort_order: new FormControl(0, [Validators.min(0)]),
		description_en: new FormControl(""),
		description_ar: new FormControl(""),
		description_he: new FormControl(""),
	});

	subscriptionForm = new FormGroup({
		merchant_id: new FormControl<number | null>(null, [Validators.required, Validators.min(1)]),
		plan_id: new FormControl<number | null>(null, [Validators.required, Validators.min(1)]),
		activation_mode: new FormControl<"admin_grant" | "payment_link">("admin_grant", [Validators.required]),
		language: new FormControl<Lang>("ar"),
	});

	constructor(
		private service: HttpHelpService,
		private spinner: NgxSpinnerService,
		private router: Router,
		private modalService: NgbModal
	) {}

	ngOnInit(): void {
		this.loadMerchants();
		this.loadPlans();
	}

	t(ar: string, en: string, he: string): string {
		if (this.curentLang === "ar") return ar;
		if (this.curentLang === "he") return he;
		return en;
	}

	get filteredPlans(): any[] {
		const keyword = (this.planSearch || "").trim().toLowerCase();
		if (!keyword) return this.tokenExtensionPlans;
		return this.tokenExtensionPlans.filter((p) => {
			const text = `${p.name_en || ""} ${p.name_ar || ""} ${p.name_he || ""} ${p.description_en || ""} ${p.description_ar || ""} ${p.description_he || ""}`.toLowerCase();
			return text.includes(keyword);
		});
	}

	isInvalid(form: FormGroup, controlName: string): boolean {
		const c = form.get(controlName);
		return !!c && c.invalid && (c.touched || c.dirty);
	}

	loadMerchants(): void {
		this.service.getAllMerchantInfo().subscribe({
			next: (res) => {
				this.merchantOptions = (res || []).map((m: any) => ({
					...m,
					display_label: this.getMerchantDisplayName(m),
				}));
			},
			error: () => (this.merchantOptions = []),
		});
	}

	openCreateTokenExtensionModal(content: any): void {
		this.subscriptionForm.patchValue({
			activation_mode: "admin_grant",
			language: this.curentLang,
		});
		this.modalService.open(content, { size: "xl", centered: true });
	}

	loadPlans(): void {
		this.spinner.show();
		this.service.getAdminV2TokenExtensionPlans().subscribe({
			next: (res) => {
				this.tokenExtensionPlans = res || [];
				this.spinner.hide();
			},
			error: (err) => {
				this.spinner.hide();
				this.showError(this.extractErrorMessage(err));
			},
		});
	}

	resetPlanForm(): void {
		this.isEditingPlan = false;
		this.editingPlanId = null;
		this.planForm.reset({
			name_en: "",
			name_ar: "",
			name_he: "",
			extra_tokens: null,
			price: null,
			is_active: true,
			sort_order: 0,
			description_en: "",
			description_ar: "",
			description_he: "",
		});
	}

	openCreatePlanModal(content: any): void {
		this.resetPlanForm();
		this.modalService.open(content, { size: "xl", centered: true });
	}

	openEditPlanModal(content: any, plan: any): void {
		this.isEditingPlan = true;
		this.editingPlanId = plan.id;
		const tokens = plan.extra_tokens ?? plan.documents_count ?? null;
		this.planForm.patchValue({
			name_en: plan.name_en || "",
			name_ar: plan.name_ar || "",
			name_he: plan.name_he || "",
			extra_tokens: tokens,
			price: Number(plan.price),
			is_active: !!plan.is_active,
			sort_order: plan.sort_order ?? 0,
			description_en: plan.description_en || "",
			description_ar: plan.description_ar || "",
			description_he: plan.description_he || "",
		});
		this.modalService.open(content, { size: "xl", centered: true });
	}

	savePlan(): void {
		if (this.planForm.invalid) {
			this.planForm.markAllAsTouched();
			this.showWarning(this.t("يرجى تعبئة الحقول المطلوبة", "Please fill required fields", "אנא מלא שדות חובה"));
			return;
		}

		const v = this.planForm.value;
		const payload = {
			name_en: (v.name_en || "").trim(),
			name_ar: (v.name_ar || "").trim() || null,
			name_he: (v.name_he || "").trim() || null,
			extra_tokens: Number(v.extra_tokens),
			price: Number(v.price),
			is_active: !!v.is_active,
			sort_order: v.sort_order == null ? 0 : Number(v.sort_order),
			description_en: (v.description_en || "").trim() || null,
			description_ar: (v.description_ar || "").trim() || null,
			description_he: (v.description_he || "").trim() || null,
		};

		this.spinner.show();
		const req$ =
			this.isEditingPlan && this.editingPlanId
				? this.service.updateAdminV2TokenExtensionPlan(this.editingPlanId, payload)
				: this.service.createAdminV2TokenExtensionPlan(payload);

		req$.subscribe({
			next: (res) => {
				this.spinner.hide();
				this.showSuccess(this.resolveMessage(res, this.t("تم حفظ الخطة", "Plan saved", "התוכנית נשמרה")));
				this.resetPlanForm();
				this.modalService.dismissAll();
				this.loadPlans();
			},
			error: (err) => {
				this.spinner.hide();
				this.showError(this.extractErrorMessage(err));
			},
		});
	}

	deletePlan(planId: number): void {
		Swal.fire({
			icon: "warning",
			title: this.t("هل أنت متأكد من حذف الخطة؟", "Are you sure to delete this plan?", "האם למחוק תוכנית זו?"),
			showCancelButton: true,
			confirmButtonText: this.t("حذف", "Delete", "מחק"),
			cancelButtonText: this.t("إلغاء", "Cancel", "בטל"),
		}).then((r) => {
			if (!r.isConfirmed) return;
			this.spinner.show();
			this.service.deleteAdminV2TokenExtensionPlan(planId).subscribe({
				next: (res) => {
					this.spinner.hide();
					this.showSuccess(this.resolveMessage(res, this.t("تم حذف الخطة", "Plan deleted", "התוכנית נמחקה")));
					this.loadPlans();
				},
				error: (err) => {
					this.spinner.hide();
					this.showError(this.extractErrorMessage(err));
				},
			});
		});
	}

	createMerchantTokenExtension(): void {
		if (this.subscriptionForm.invalid) {
			this.subscriptionForm.markAllAsTouched();
			this.showWarning(this.t("يرجى تعبئة الحقول المطلوبة", "Please fill required fields", "אנא מלא שדות חובה"));
			return;
		}

		const v = this.subscriptionForm.value;
		const payload = {
			merchant_id: Number(v.merchant_id),
			plan_id: Number(v.plan_id),
			activation_mode: v.activation_mode,
			language: v.language || this.curentLang,
		};

		this.spinner.show();
		this.service.createAdminMerchantTokenExtension(payload).subscribe({
			next: (res) => {
				this.spinner.hide();
				if (res?.type === "payment_link" && res?.payment_url) {
					this.lastPaymentUrl = res.payment_url;
					this.lastOrderId = res.order_id || null;
					Swal.fire({
						icon: "success",
						title: this.resolveMessage(res, this.t("تم إنشاء رابط الدفع", "Payment link created", "נוצר קישור תשלום")),
						html: `<div style="word-break:break-all">${res.payment_url}</div>`,
						showDenyButton: true,
						showCancelButton: true,
						confirmButtonText: this.t("فتح الرابط", "Open Link", "פתח קישור"),
						denyButtonText: this.t("نسخ الرابط", "Copy Link", "העתק קישור"),
						cancelButtonText: this.t("إغلاق", "Close", "סגור"),
					}).then((result) => {
						if (result.isConfirmed) window.open(res.payment_url, "_blank");
						if (result.isDenied) this.copyPaymentLink(res.payment_url);
					});
				} else {
					this.showSuccess(this.resolveMessage(res, this.t("تم تفعيل الامتداد", "Extension activated", "ההרחבה הופעלה")));
				}
				this.subscriptionForm.patchValue({
					activation_mode: "admin_grant",
					language: this.curentLang,
				});
				this.subscriptionForm.get("merchant_id")?.reset();
				this.subscriptionForm.get("plan_id")?.reset();
				this.modalService.dismissAll();
			},
			error: (err) => {
				this.spinner.hide();
				this.showError(this.extractErrorMessage(err));
			},
		});
	}

	copyPaymentLink(url?: string): void {
		const link = url || this.lastPaymentUrl;
		if (!link) return;
		if (navigator?.clipboard?.writeText) {
			navigator.clipboard.writeText(link).then(() => {
				this.showSuccess(this.t("تم نسخ الرابط", "Link copied", "הקישור הועתק"));
			}).catch(() => this.copyFallback(link));
			return;
		}
		this.copyFallback(link);
	}

	getMerchantDisplayName(merchant: any): string {
		if (!merchant) return "-";
		if (this.curentLang === "ar") {
			return merchant.merchant_name_ar || merchant.merchant_name_en || merchant.merchant_name_he || `#${merchant.merchant_id}`;
		}
		if (this.curentLang === "he") {
			return merchant.merchant_name_he || merchant.merchant_name_en || merchant.merchant_name_ar || `#${merchant.merchant_id}`;
		}
		return merchant.merchant_name_en || merchant.merchant_name_ar || merchant.merchant_name_he || `#${merchant.merchant_id}`;
	}

	getPlanDisplayName(plan: any): string {
		if (!plan) return "-";
		if (this.curentLang === "ar") return plan.name_ar || plan.name_en || plan.name_he || `#${plan.id}`;
		if (this.curentLang === "he") return plan.name_he || plan.name_en || plan.name_ar || `#${plan.id}`;
		return plan.name_en || plan.name_ar || plan.name_he || `#${plan.id}`;
	}

	getPlanOptionLabel(plan: any): string {
		if (!plan) return "-";
		const tokens = plan.extra_tokens ?? plan.documents_count;
		const parts: string[] = [];
		if (tokens != null) {
			parts.push(`${this.t("توكنات", "Tokens", "טוקנים")}: ${tokens}`);
		}
		const details = parts.length ? ` — ${parts.join(" | ")}` : "";
		return `${this.getPlanDisplayName(plan)}${details}`;
	}

	merchantSearchFn = (term: string, item: any): boolean => {
		const source = item?.value ?? item ?? {};
		const haystack = [
			source.merchant_name_ar,
			source.merchant_name_en,
			source.merchant_name_he,
			source.id_number,
			source.merchant_id,
		]
			.filter((v) => v !== null && v !== undefined)
			.join(" ")
			.toLowerCase();
		return haystack.includes((term || "").toLowerCase());
	};

	goBack(): void {
		this.router.navigate(["/dashboard/plans-info"]);
	}

	private copyFallback(link: string): void {
		const ta = document.createElement("textarea");
		ta.value = link;
		ta.style.position = "fixed";
		ta.style.opacity = "0";
		document.body.appendChild(ta);
		ta.select();
		document.execCommand("copy");
		document.body.removeChild(ta);
		this.showSuccess(this.t("تم نسخ الرابط", "Link copied", "הקישור הועתק"));
	}

	private resolveMessage(res: any, fallback: string): string {
		if (this.curentLang === "ar" && res?.messageAr) return res.messageAr;
		if (this.curentLang === "he" && res?.messageHe) return res.messageHe;
		return res?.messageEn || res?.message || fallback;
	}

	private extractErrorMessage(error: any): string {
		const e = error?.error;
		if (this.curentLang === "ar" && e?.messageAr) return e.messageAr;
		if (this.curentLang === "he" && e?.messageHe) return e.messageHe;
		if (this.curentLang === "en" && e?.messageEn) return e.messageEn;
		if (Array.isArray(e?.message)) return e.message.join(", ");
		if (typeof e?.message === "string") return e.message;
		return this.t("حدث خطأ غير متوقع", "Unexpected error occurred", "אירעה שגיאה לא צפויה");
	}

	private showSuccess(title: string): void {
		Swal.fire({ icon: "success", title });
	}

	private showError(title: string): void {
		Swal.fire({ icon: "error", title });
	}

	private showWarning(title: string): void {
		Swal.fire({ icon: "warning", title });
	}
}
